<!--
  A contact's balance is an account balance - a level, never a flow - so it is never given
  a `+` (AGENTS.md: "A level ... is never signed - omit this option"); a negative amount
  still carries its own minus glyph. Colour follows `--color-positive`/`--color-negative` - the
  sign of a number, never an entry kind or an ok/danger outcome - so KindIcon/StatusTag are the
  wrong carriers here, per the colour vocabulary in packages/ui/src/tokens.css.

  `contacts_balances` returns one net figure per currency, never a gross receivable and a gross
  payable - so "They owe you" and "You owe them" below are the same net figure read through its
  own sign (one of the two is always zero), not two independently summed totals. Each currency
  renders as its own block and nothing sums across currencies.
-->
<script setup lang="ts">
import MoneyAmount from '@/app/components/MoneyAmount.vue'
import { absMoney, signOfMoney, zeroMoney, type Money } from '@taler/domain'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps<{ balances: readonly Money[] }>()
const { t } = useI18n()

const rows = computed(() =>
  props.balances.map((balance) => {
    const sign = signOfMoney(balance)
    return {
      key: balance.currency,
      settled: sign === 0,
      net: balance,
      tone: sign > 0 ? ('positive' as const) : sign < 0 ? ('negative' as const) : undefined,
      owedToYou: sign > 0 ? balance : zeroMoney(balance.currency),
      owedByYou: sign < 0 ? absMoney(balance) : zeroMoney(balance.currency),
    }
  }),
)
</script>

<template>
  <ul class="flex flex-col divide-y divide-[color:var(--color-border)]">
    <li
      v-for="row in rows"
      :key="row.key"
      class="flex flex-col gap-[var(--space-inline)] py-[var(--space-cell)]"
    >
      <div v-if="row.settled" class="flex items-center justify-between gap-[var(--space-cell)]">
        <span class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">{{
          t('contacts.settled')
        }}</span>
        <MoneyAmount :value="row.net" />
      </div>
      <template v-else>
        <div class="flex items-center justify-between gap-[var(--space-cell)]">
          <div class="flex flex-col">
            <span class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">{{
              t('contacts.netBalance')
            }}</span>
            <span class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">{{
              t('contacts.netBalanceExplain')
            }}</span>
          </div>
          <MoneyAmount :value="row.net" :tone="row.tone" size="large" />
        </div>
        <div class="flex items-center justify-between gap-[var(--space-cell)]">
          <span class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">{{
            t('contacts.owesYou')
          }}</span>
          <MoneyAmount :value="row.owedToYou" />
        </div>
        <div class="flex items-center justify-between gap-[var(--space-cell)]">
          <span class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">{{
            t('contacts.youOwe')
          }}</span>
          <MoneyAmount :value="row.owedByYou" />
        </div>
      </template>
    </li>
  </ul>
</template>
